import { gql } from '@apollo/client';
import { Alert } from 'components/alert';
import { Button } from 'components/buttons/button';
import {
  SaveNewShippingOptionMutationVariables,
  useGetAllCountriesAndRegionsQuery,
  useSaveNewShippingOptionMutation,
} from 'lib/graphql-generated';
import { useInput } from 'lib/hooks/use-input';
import { useState } from 'react';
import { addToState, removeFromState } from 'lib/modify-state-arrays';
import { isNumber } from 'lib/utils';
import currency from 'currency.js';

const GET_ALL_COUNTRIES_AND_REGIONS = gql`
  query getAllCountriesAndRegions {
    regions {
      name
      code
    }

    countries {
      name
      code
    }
  }
`;

const SAVE_NEW_SHIPPING_OPTION = gql`
  mutation SaveNewShippingOption(
    $shippingOption: shipping_options_insert_input!
  ) {
    insert_shipping_options_one(object: $shippingOption) {
      __typename
      id
      description
      from
      to
      price
      region
      country
    }
  }
`;

interface DestinationObject {
  type: 'country' | 'region';
  code: string;
}

function deserializeDestination(value: string): DestinationObject {
  const parts = value.split('_');
  return {
    code: parts[1],
    type: parts[0] as 'country' | 'region',
  };
}

interface Props {
  handleSuccess: () => void;
}

export const NewShippingOption = ({ handleSuccess }: Props) => {
  const { data, loading, error } = useGetAllCountriesAndRegionsQuery();
  const [
    saveNewOption,
    { data: createdData, loading: saving, error: errorCreating },
  ] = useSaveNewShippingOptionMutation({
    refetchQueries: ['GetShippingBlockData'],
    onCompleted: () => handleSuccess(),
  });
  const [errors, setErrors] = useState<string[]>([]);
  const name = useInput<string>('');
  const description = useInput('');
  const from = useInput<number>(0);
  const to = useInput<number | string>('');
  const price = useInput<number>(0);
  const destination = useInput<string>('');

  const handleSave = (e) => {
    e.preventDefault();
    setErrors([]);
    const newErrors = [];

    if (from.value < 0) {
      newErrors.push('"From" must be 0 or more.');
    }

    if (to.value !== '') {
      if (to.value < 1) {
        newErrors.push('"To" must be empty OR more than 1');
      }

      if (from.value > to.value) {
        newErrors.push('"From" must be less or equal to the "To" value.');
      }
    }

    if (name.value.length < 2) {
      newErrors.push('"Name" must be at least 2 characters long');
    } else if (name.value.length > 50) {
      newErrors.push('"Name" must be less than 50 characters long');
    }

    try {
      currency(price.value);
    } catch (e) {
      newErrors.push('"Price" must be a valid number');
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
    } else {
      const destObj = deserializeDestination(destination.value);
      const vars: SaveNewShippingOptionMutationVariables = {
        shippingOption: {
          description: description.value,
          name: name.value,
          from: from.value,
          to: to.value === '' ? null : (to.value as number),
          price: currency(price.value).intValue,
          region: destObj.type === 'region' ? destObj.code : null,
          country: destObj.type === 'country' ? destObj.code : null,
        },
      };
      saveNewOption({ variables: vars });
    }
  };

  return (
    <form className='[ p-4 sm:p-6 ] [ flex flex-row flex-wrap space-y-4 ] bg-white shadow rounded-md'>
      {errors.length > 0 && <Alert errors={errors} />}
      <label
        htmlFor='name'
        className='w-full text-sm font-medium text-gray-700'>
        Name
        <input
          {...name.bind}
          type='text'
          name='name'
          id='name'
          autoComplete='off'
          autoCorrect='off'
          className='mt-1 focus:ring-green-default focus:border-green-default block w-full shadow-sm sm:text-sm border-gray-300 rounded-md'
        />
      </label>
      <fieldset className='flex flex-row space-x-4 w-full'>
        <label
          htmlFor='from'
          className='block text-sm font-medium text-gray-700 flex-1'>
          From
          <input
            {...from.bind}
            type='number'
            name='from'
            id='from'
            min={0}
            className='mt-1 focus:ring-green-default focus:border-green-default block w-full shadow-sm sm:text-sm border-gray-300 rounded-md'
          />
        </label>

        <label
          htmlFor='to'
          className='block text-sm font-medium text-gray-700 flex-1'>
          To
          <input
            {...to.bind}
            type='number'
            name='to'
            id='to'
            min={1}
            className='mt-1 focus:ring-green-default focus:border-green-default block w-full shadow-sm sm:text-sm border-gray-300 rounded-md'
          />
        </label>

        <label
          htmlFor='price'
          className='block text-sm font-medium text-gray-700 flex-1'>
          Price
          <input
            {...price.bind}
            type='number'
            step='.01'
            min={0}
            name='price'
            id='price'
            className='mt-1 focus:ring-green-default focus:border-green-default block w-full shadow-sm sm:text-sm border-gray-300 rounded-md'
          />
        </label>
      </fieldset>
      <fieldset className='w-full flex flow-row space-x-4'>
        <label
          htmlFor='desc'
          className='flex-1 text-sm font-medium text-gray-700'>
          Short Description
          <input
            {...description.bind}
            type='text'
            name='desc'
            id='desc'
            className='mt-1 focus:ring-green-default focus:border-green-default block w-full shadow-sm sm:text-sm border-gray-300 rounded-md'
          />
        </label>
        <label
          htmlFor='desc'
          className='flex-1 text-sm font-medium text-gray-700'>
          Destination
          <select
            {...destination.bind}
            id='destination'
            className='mt-1 focus:ring-green-default focus:border-green-default block w-full shadow-sm sm:text-sm border-gray-300 rounded-md'>
            <option value='' disabled className='text-gray-500'>
              Select a target country/region
            </option>
            <optgroup label='Regions'>
              {data?.regions.map((r) => (
                <option key={`region_${r.code}`} value={`region_${r.code}`}>
                  {r.name}
                </option>
              ))}
            </optgroup>
            <option value='' disabled></option>
            <optgroup label='Countries'>
              {data?.countries.map((c) => (
                <option key={`country_${c.code}`} value={`country_${c.code}`}>
                  {c.name}
                </option>
              ))}
            </optgroup>
          </select>
        </label>
      </fieldset>
      <Button
        buttonType='primary'
        buttonSize='md'
        className='ml-auto'
        onClick={handleSave}
        disabled={
          !name.isModified ||
          !price.isModified ||
          !destination.isModified ||
          !description.isModified
        }>
        {saving ? (
          <>
            <svg
              className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'>
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'></circle>
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
            </svg>
            Saving
          </>
        ) : (
          'Save'
        )}
      </Button>
    </form>
  );
};
